import React, { useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import CommonFilter from "@/components/common/CommonFilter";
import CommonTable from "@/components/common/CommonTable";
import TablePagination from "@/components/common/TablePagination";
import SalaryConfigTable from "@/components/common/SalaryConfigTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, Layers } from "lucide-react";
import moment from "moment";
import { useCurrentUser } from "@/hooks/useCurrentUser";

import { useSalaryPreviewDraft } from "./hooks/useSalaryPreviewDraft";
import { useSalaryPreviewData } from "./hooks/useSalaryPreviewData";
import { useSalaryPreviewActions } from "./hooks/useSalaryPreviewActions";
import { getAttendanceColumns } from "./components/attendanceColumns";
import { SalaryPreviewHeader } from "./components/SalaryPreviewHeader";
import { DraftRestoredBanner } from "./components/DraftRestoredBanner";


type LocationState = {
  userIds?: string[];
  startDate?: string;
  endDate?: string;
  siteId?: string;
} | null;

export const MonthlySalaryPreview: React.FC = () => {
  const location = useLocation();
  const { siteId: userSiteId } = useCurrentUser();

  const locationState = location.state as LocationState;

  // Resolve initial date range from navigation state or sensible defaults.
  // Use primitive deps (strings) instead of locationState object to keep memoization stable.
  const locStartDate = locationState?.startDate;
  const locEndDate = locationState?.endDate;
  const initialDates = useMemo(() => {
    const today = moment();
    return {
      startDate:
        locStartDate ||
        today.clone().subtract(1, "month").date(20).format("YYYY-MM-DD"),
      endDate: locEndDate || today.clone().date(20).format("YYYY-MM-DD"),
    };
  }, [locStartDate, locEndDate]);

  // 1. Draft persistence hook
  const draft = useSalaryPreviewDraft({
    siteId: locationState?.siteId,
    userSiteId,
    startDate: initialDates.startDate,
    endDate: initialDates.endDate,
  });

  // 2. Data fetching, filtering, pagination & transforms hook
  const data = useSalaryPreviewData({
    locationState,
    userSiteId,
    initialDates,
    draft,
  });

  // 3. Salary change, save, and create-salary actions hook
  const actions = useSalaryPreviewActions({
    previewData: data.previewData,
    setPreviewData: data.setPreviewData,
    locationState,
    userSiteId,
    startDate: data.filter.startDate || initialDates.startDate,
    endDate: data.filter.endDate || initialDates.endDate,
    draft,
  });

  /**
   * Clear draft AND re-fetch fresh preview data so the table
   * reflects original API values (not stale draft values).
   */
  const handleClearDraftAndRefresh = useCallback(() => {
    draft.handleClearDraft();
    data.handleFetchPreview();
  }, [draft.handleClearDraft, data.handleFetchPreview]);

  // Keep tab state at top with other hooks (Rules of Hooks)
  const [activeTab, setActiveTab] = React.useState<string>("attendance");

  // Attendance tab columns — recomputed only when pagination changes
  const attendanceColumns = useMemo(
    () => getAttendanceColumns(data.paginationData),
    [data.paginationData]
  );

  const formattedPeriod =
    data.filter.startDate && data.filter.endDate
      ? `${moment(data.filter.startDate).format("DD MMM YYYY")} - ${moment(
          data.filter.endDate
        ).format("DD MMM YYYY")}`
      : "Selected Period";

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header */}
        <SalaryPreviewHeader
          formattedPeriod={formattedPeriod}
          allUsersCount={data.allUsers.length}
          errorsCount={data.errors.length}
          isLoading={data.isLoading}
          isSubmitting={actions.isSubmitting}
          onRefresh={data.handleFetchPreview}
          onCreateSalary={actions.handleCreateSalary}
        />

        {/* Draft Restored Notification */}
        {draft.isDraftActive && (
          <DraftRestoredBanner
            draftTimestamp={draft.draftTimestamp}
            onClearDraft={handleClearDraftAndRefresh}
          />
        )}

        {/* Filters */}
        <CommonFilter
          startDate={data.filter.startDate || ""}
          endDate={data.filter.endDate || ""}
          onDateRangeChange={data.handleDateRangeChange}
          searchValue={data.filter.search || ""}
          onSearchChange={data.handleSearchChange}
          searchPlaceholder="Search employee name or code..."
        />

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full space-y-4"
        >
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger
              value="attendance"
              className="text-xs font-semibold px-4 py-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-xs"
            >
              <Users className="w-3.5 h-3.5 mr-2 text-slate-500" />
              Attendance &amp; Summary
            </TabsTrigger>
            <TabsTrigger
              value="salary_matrix"
              className="text-xs font-semibold px-4 py-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-xs"
            >
              <Layers className="w-3.5 h-3.5 mr-2 text-slate-500" />
              Salary Heads Matrix
            </TabsTrigger>
          </TabsList>

          {/* Tab 1: Attendance Summary */}
          <TabsContent value="attendance" className="m-0 space-y-4">
            <CommonTable
              columns={attendanceColumns}
              data={data?.paginatedUsers}
              loading={data?.isLoading}
            />
            <TablePagination
              paginationData={data.paginationData}
              setPaginationData={data.setPaginationData}
              handleChangePageSize={data.handleChangePageSize}
              isLoading={data.isLoading}
            />
          </TabsContent>

          {/* Tab 2: Salary Heads Matrix */}
          <TabsContent value="salary_matrix" className="m-0 space-y-4">
            <SalaryConfigTable
              data={data.backupUsersData}
              salaryHead={data.previewSalaryHeads}
              loading={data.isLoading}
              stickyColumns={3}
              currentPage={data.paginationData.currentPage}
              pageSize={data.paginationData.pageSize}
              emptyMessage="No salary preview data available"
              onSaveUserSalary={actions.handleSaveUserSalary}
              onSalaryChange={actions.handleSalaryChange}
            />
            <TablePagination
              paginationData={data.paginationData}
              setPaginationData={data.setPaginationData}
              handleChangePageSize={data.handleChangePageSize}
              isLoading={data.isLoading}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default MonthlySalaryPreview;
